import type { ChatStreamCallbacks, SendChatMessageParams } from "../types";
import { parseSseLine } from "./decode";

import { instance } from "@/lib/axios/instance";

const READ_TIMEOUT_MS = 30_000;

export async function streamChat(
  params: SendChatMessageParams,
  callbacks: ChatStreamCallbacks,
  signal?: AbortSignal,
): Promise<void> {
  const response = await instance.post("/api/v2/captain/chat", params, {
    responseType: "stream",
    adapter: "fetch",
    signal,
    headers: { Accept: "text/event-stream" },
  });

  const reader = (response.data as ReadableStream<Uint8Array>).getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let receivedDone = false;

  try {
    while (true) {
      const readPromise = reader.read();
      let timerId: ReturnType<typeof setTimeout>;
      const timeoutPromise = new Promise<never>((_, reject) => {
        timerId = setTimeout(() => reject(new Error("Stream read timeout")), READ_TIMEOUT_MS);
      });

      let result: ReadableStreamReadResult<Uint8Array>;
      try {
        result = await Promise.race([readPromise, timeoutPromise]);
      } finally {
        clearTimeout(timerId!);
      }

      const { done, value } = result;
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        if (parseSseLine(line, callbacks)) {
          receivedDone = true;
          return;
        }
      }
    }

    // Process any remaining buffer after stream ends
    if (buffer.trim()) {
      parseSseLine(buffer, callbacks);
    }

    if (!receivedDone) {
      callbacks.onError("Captain.StreamClosed", "Connection closed unexpectedly. Please try again.");
    }
  } finally {
    reader.releaseLock();
  }
}
